import { useEffect, useRef, useState } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useSpring,
    useMotionValue,
    useMotionValueEvent,
    AnimatePresence,
} from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { STAGES, scrollToId } from "@/data";

const clamp = (v, a, b) => Math.min(Math.max(v, a), b);
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = (t) =>
    t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

export default function Hero() {
    const wrapRef = useRef(null);
    const dragRef = useRef(null);
    const [vp, setVp] = useState({ w: 1440, h: 900 });
    const [stage, setStage] = useState(0);
    const [hovering, setHovering] = useState(false);
    const [overButton, setOverButton] = useState(false);
    const [finePointer, setFinePointer] = useState(false);

    useEffect(() => {
        const onResize = () =>
            setVp({ w: window.innerWidth, h: window.innerHeight });
        onResize();
        window.addEventListener("resize", onResize);
        setFinePointer(window.matchMedia("(pointer: fine)").matches);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const R0 = Math.min(vp.w, vp.h) * 0.16;
    const R1 = Math.hypot(vp.w, vp.h) * 0.62;

    const { scrollYProgress } = useScroll({
        target: wrapRef,
        offset: ["start start", "end end"],
    });
    const journey = useSpring(
        useTransform(scrollYProgress, [0, 1], [0, 2]),
        { stiffness: 110, damping: 28, mass: 0.6 },
    );

    useMotionValueEvent(journey, "change", (v) => {
        const s = Math.round(clamp(v, 0, 2));
        setStage((prev) => (prev === s ? prev : s));
    });

    const portalX = vp.w >= 1024 ? 66 : 50;
    const circleAt = (v, i) => {
        const t = easeInOut(clamp(v - (i - 1), 0, 1));
        return `circle(${lerp(R0, R1, t)}px at ${portalX}% 50%)`;
    };

    const clip1 = useTransform(journey, (v) => circleAt(v, 1));
    const clip2 = useTransform(journey, (v) => circleAt(v, 2));
    const opacity1 = useTransform(journey, [0, 0.015], [0, 1]);
    const opacity2 = useTransform(journey, [1, 1.015], [0, 1]);
    const scale0 = useTransform(journey, [0, 2], [1, 1.14]);
    const scale1 = useTransform(journey, [0, 1], [1.24, 1]);
    const scale2 = useTransform(journey, [1, 2], [1.24, 1]);

    const ringSize = useTransform(journey, (v) => {
        const seg = Math.min(Math.floor(clamp(v, 0, 1.999)), 1);
        const t = easeInOut(clamp(v - seg, 0, 1));
        return lerp(R0, R1, t) * 2;
    });
    const ringOpacity = useTransform(journey, (v) =>
        v < 0.04 ? v / 0.04 : v > 1.82 ? clamp((2 - v) / 0.18, 0, 1) : 1,
    );

    const titleOpacity = useTransform(journey, [0, 0.3], [1, 0]);
    const titleY = useTransform(journey, [0, 0.3], [0, -60]);
    const progressX = useTransform(journey, [0, 2], [0, 1]);

    // custom lens cursor
    const cx = useMotionValue(-200);
    const cy = useMotionValue(-200);
    const cursorX = useSpring(cx, { stiffness: 500, damping: 45 });
    const cursorY = useSpring(cy, { stiffness: 500, damping: 45 });

    useEffect(() => {
        const onMove = (e) => {
            cx.set(e.clientX);
            cy.set(e.clientY);
            setOverButton(!!e.target.closest?.("button, a, input, textarea, select, [role='button'], [data-interactive]"));
            if (dragRef.current) {
                const target =
                    dragRef.current.scroll +
                    (dragRef.current.y - e.clientY) * 2.6;
                if (window.__lenis)
                    window.__lenis.scrollTo(target, { immediate: true });
                else window.scrollTo(0, target);
            }
        };
        const onUp = () => (dragRef.current = null);
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
    }, [cx, cy]);

    const goToStage = (i) => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const top = wrap.offsetTop;
        const dist = wrap.offsetHeight - window.innerHeight;
        const target = top + (i / 2) * dist;
        if (window.__lenis) window.__lenis.scrollTo(target, { duration: 1.8 });
        else window.scrollTo({ top: target, behavior: "smooth" });
    };

    const layers = [
        { img: STAGES[0].img, style: { scale: scale0 }, testid: "hero-stage-interior" },
        { img: STAGES[1].img, style: { scale: scale1, clipPath: clip1, opacity: opacity1 }, testid: "hero-stage-exterior" },
        { img: STAGES[2].img, video: STAGES[2].video_url, style: { scale: scale2, clipPath: clip2, opacity: opacity2 }, testid: "hero-stage-aerial" },
    ];

    return (
        <section
            ref={wrapRef}
            data-testid="hero-journey"
            className="relative h-[340vh] bg-[#121211]"
        >
            <div
                className="hero-viewport lens-cursor-area sticky top-0 h-screen select-none overflow-hidden"
                onPointerDown={(e) => {
                    if (e.pointerType !== "mouse" || e.target.closest?.("button, a, input, textarea, select, [role='button'], [data-interactive]")) return;
                    dragRef.current = { y: e.clientY, scroll: window.scrollY };
                }}
                onPointerEnter={() => setHovering(true)}
                onPointerLeave={() => {
                    setHovering(false);
                    dragRef.current = null;
                }}
            >
                {/* image layers */}
                {layers.map((l) => (
                    <motion.div
                        key={l.testid}
                        data-testid={l.testid}
                        className="absolute inset-0 will-change-transform"
                        style={l.style}
                    >
                        {l.video ? (
                            <video
                                data-testid="hero-aerial-video"
                                src={l.video}
                                autoPlay
                                muted
                                loop
                                playsInline
                                draggable={false}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <img
                                src={l.img}
                                alt=""
                                draggable={false}
                                className="h-full w-full object-cover"
                            />
                        )}
                        <div className="absolute inset-0 bg-black/25" />
                    </motion.div>
                ))}

                <div className="vignette absolute inset-0" />
                <div className="noise-overlay absolute inset-0 opacity-[0.08] mix-blend-overlay" />

                {/* optical portal ring */}
                <motion.div
                    className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                    style={{ width: ringSize, height: ringSize, opacity: ringOpacity, left: `${portalX}%` }}
                    data-testid="hero-portal-ring"
                >
                    <div className="absolute inset-0 rounded-full border border-white/50" />
                    <div className="spin-slow absolute -inset-3 rounded-full border border-dashed border-white/25" />
                    <div className="absolute -top-7 left-1/2 h-4 w-px -translate-x-1/2 bg-white/50" />
                    <div className="absolute -bottom-7 left-1/2 h-4 w-px -translate-x-1/2 bg-white/50" />
                    <div className="absolute -left-7 top-1/2 h-px w-4 -translate-y-1/2 bg-white/50" />
                    <div className="absolute -right-7 top-1/2 h-px w-4 -translate-y-1/2 bg-white/50" />
                </motion.div>

                {/* kinetic headline */}
                <motion.div
                    className="hero-copy pointer-events-none absolute inset-0 flex flex-col items-start justify-center px-6 text-left lg:px-10"
                    style={{ opacity: titleOpacity, y: titleY }}
                >
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.9 }}
                        className="hero-eyebrow font-mono2 text-[10px] uppercase leading-relaxed tracking-[0.34em] text-white/70 sm:text-[11px]"
                        data-testid="hero-eyebrow"
                    >
                        Immersive 360° walkthroughs
                        <br />
                        Aerial & drone cinematography
                    </motion.p>
                    <h1 data-testid="hero-headline" className="hero-heading font-display font-light leading-[0.98] tracking-tight text-[#FAF8F5]">
                        {["From inside.", "To outside.", "To above."].map(
                            (line, i) => (
                                <span key={line} className="block overflow-hidden pb-1">
                                    <motion.span
                                        initial={{ y: "115%" }}
                                        animate={{ y: 0 }}
                                        transition={{
                                            duration: 1.15,
                                            delay: 0.25 + i * 0.14,
                                            ease: [0.16, 1, 0.3, 1],
                                        }}
                                        className={`block ${i === 2 ? "italic" : ""}`}
                                        data-testid={`hero-line-${i}`}
                                    >
                                        {line}
                                    </motion.span>
                                </span>
                            ),
                        )}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
                        className="hero-intro max-w-sm text-base font-light leading-relaxed text-white/80"
                        data-testid="hero-intro"
                    >
                        Immersive visual experiences that let people explore a
                        space — inside and out — before they arrive.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
                        className="hero-actions pointer-events-auto flex flex-wrap items-center gap-x-8 gap-y-3"
                    >
                        <button
                            data-testid="hero-cta"
                            data-interactive
                            onClick={() => scrollToId("contact")}
                            onPointerDown={(e) => e.stopPropagation()}
                            className="project-cta project-cta-light group relative inline-flex items-center gap-3 overflow-hidden rounded-full border border-[#FAF8F5] bg-[#FAF8F5] px-7 py-3.5 font-mono2 text-[11px] uppercase tracking-[0.26em] text-[#121211]"
                        >
                            Start a project
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </button>
                        <span
                            className="pulse-soft hidden font-mono2 text-[10px] uppercase tracking-[0.34em] text-white/50 lg:block"
                            data-testid="hero-hint"
                        >
                            Scroll or drag to travel
                        </span>
                    </motion.div>
                </motion.div>

                {/* HUD bottom bar */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-5 px-6 pb-6 lg:px-10 lg:pb-8"
                >
                    <div className="min-w-[140px]">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={stage}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                                data-testid="hero-stage-label"
                            >
                                <p className="font-mono2 text-[10px] tracking-[0.3em] text-white/50">
                                    {STAGES[stage].index} / 03
                                </p>
                                <p className="mt-1 font-display text-2xl italic text-[#FAF8F5] sm:text-3xl">
                                    {STAGES[stage].sub}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="hidden flex-col items-center gap-3 sm:flex">
                        <div className="h-px w-40 overflow-hidden bg-white/20 lg:w-64">
                            <motion.div
                                className="h-full origin-left bg-white/80"
                                style={{ scaleX: progressX }}
                                data-testid="hero-progress-bar"
                            />
                        </div>
                        <div className="flex gap-5">
                            {STAGES.map((s, i) => (
                                <button
                                    key={s.id}
                                    data-testid={`hero-stage-btn-${s.id}`}
                                    onClick={() => goToStage(i)}
                                    className={`font-mono2 text-[10px] tracking-[0.26em] transition-colors duration-300 ${
                                        stage === i
                                            ? "text-white"
                                            : "text-white/40 hover:text-white/75"
                                    }`}
                                >
                                    {s.index} {s.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <p className="hidden min-w-[140px] text-right font-mono2 text-[10px] tracking-[0.3em] text-white/40 lg:block">
                        34.05° N — 118.25° W
                    </p>
                </motion.div>

                {/* lens cursor */}
                {finePointer && (
                    <motion.div
                        className="pointer-events-none fixed left-0 top-0 z-40"
                        style={{ x: cursorX, y: cursorY, opacity: overButton ? 0 : 1, pointerEvents: "none" }}
                        aria-hidden="true"
                    >
                        <div
                            className={`flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border transition-[width,height,background-color,opacity] duration-300 ${
                                !hovering
                                    ? "h-10 w-10 border-white/0 opacity-0"
                                    : overButton
                                      ? "h-3 w-3 border-white bg-white opacity-100"
                                      : "h-20 w-20 border-white/70 opacity-100"
                            }`}
                            data-testid="hero-lens-cursor"
                            data-active={overButton}
                        >
                            {!overButton && (
                                <span className="font-mono2 text-[9px] tracking-[0.3em] text-white/80">
                                    DRAG
                                </span>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
